// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { InventorySubmitCallback, MyShellInventoryDataForm } from "./shell/MyShellInventoryDataForm";
import { ProductType } from "../../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { MY_INVENTORY_DATA_FORM_CLASS_NAME } from "../../../../../../core/constants/className";
import { Loader } from "../../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { InventoryState } from "../../../../../../../../io/hyperify/core/store/types/inventory/InventoryState";
import { InventoryData } from "../../../../../../../../io/hyperify/core/store/types/inventory/data/InventoryData";
import { PermissionList } from "../../../../../../../../io/hyperify/core/PermissionUtils";
import { SendanorApiPermission } from "../../../../../../core/types/SendanorApiPermission";
import { SelectFieldItem } from "../../../../../../../../io/hyperify/frontend/types/items/SelectFieldModel";
import "./MyInventoryDataForm.scss";
import { MyEmailInventoryDataForm } from "./email/MyEmailInventoryDataForm";
import { MyVirtualServerInventoryDataForm } from "./virtualServer/MyVirtualServerInventoryDataForm";
import { MyWebHotelInventoryDataForm } from "./webHotel/MyWebHotelInventoryDataForm";
import { MyDomainInventoryDataForm } from "./domain/MyDomainInventoryDataForm";
import { MyDomainTransferInventoryDataForm } from "./domainTransfer/MyDomainTransferInventoryDataForm";

export interface MyInventoryDataFormProps {

    readonly className ?: string;
    readonly type      ?: ProductType;
    readonly state      : InventoryState;
    readonly value     ?: InventoryData;
    readonly submit    ?: InventorySubmitCallback;
    readonly permissions : PermissionList<SendanorApiPermission>;

    readonly shellIncorrectDataLabel  : string;
    readonly shellStateLabel          : string;
    readonly shellHostnameLabel       : string;
    readonly shellPortLabel           : string;
    readonly shellUsernameLabel       : string;
    readonly shellUsernamePlaceholder : string;
    readonly shellRealNameLabel       : string;
    readonly shellRealNameDisclaimer ?: string;
    readonly shellResetLabel          : string;
    readonly shellSaveLabel           : string;
    readonly shellApproveLabel        : string;

    readonly emailIncorrectDataLabel  : string;
    readonly emailStateLabel          : string;
    readonly emailHostnameLabel       : string;
    readonly emailUsernameLabel       : string;
    readonly emailUsernamePlaceholder : string;
    readonly emailResetLabel          : string;
    readonly emailSaveLabel           : string;
    readonly emailApproveLabel        : string;

    readonly virtualServerIncorrectDataLabel  : string;
    readonly virtualServerStateLabel          : string;
    readonly virtualServerSystemLabel       : string;
    readonly virtualServerNameLabel       : string;
    readonly virtualServerNamePlaceholder : string;
    readonly virtualServerResetLabel          : string;
    readonly virtualServerSaveLabel           : string;
    readonly virtualServerApproveLabel        : string;

    readonly webHotelIncorrectDataLabel  : string;
    readonly webHotelStateLabel          : string;
    readonly webHotelSystemLabel       : string;
    readonly webHotelNameLabel       : string;
    readonly webHotelNamePlaceholder : string;
    readonly webHotelResetLabel          : string;
    readonly webHotelSaveLabel           : string;
    readonly webHotelApproveLabel        : string;

    readonly domainIncorrectDataLabel  : string;
    readonly domainStateLabel          : string;
    readonly domainSystemLabel       : string;
    readonly domainNameLabel       : string;
    readonly domainResetLabel          : string;
    readonly domainSaveLabel           : string;
    readonly domainApproveLabel        : string;

    readonly domainTransferIncorrectDataLabel  : string;
    readonly domainTransferStateLabel          : string;
    readonly domainTransferSystemLabel       : string;
    readonly domainTransferNameLabel       : string;
    readonly domainTransferResetLabel          : string;
    readonly domainTransferSaveLabel           : string;
    readonly domainTransferApproveLabel        : string;
    readonly domainTransferAuthIdLabel        : string;

    readonly stateValues : readonly SelectFieldItem<InventoryState>[];

}

export function MyInventoryDataForm (props: MyInventoryDataFormProps): any {
    const className = props?.className;
    const submit = props?.submit;
    const permissions = props?.permissions;
    const stateValues = props?.stateValues;
    const classNames = (
        MY_INVENTORY_DATA_FORM_CLASS_NAME
        + (className ? ` ${className}` : '')
    );
    const value = props?.value;
    const state = props.state;
    const type = props.type;

    const shellIncorrectDataLabel = props?.shellIncorrectDataLabel;
    const shellHostnameLabel = props?.shellHostnameLabel;
    const shellPortLabel = props?.shellPortLabel;
    const shellUsernameLabel = props?.shellUsernameLabel;
    const shellUsernamePlaceholder = props?.shellUsernamePlaceholder;
    const shellRealNameLabel = props?.shellRealNameLabel;
    const shellRealNameDisclaimer = props?.shellRealNameDisclaimer;
    const shellResetLabel = props?.shellResetLabel;
    const shellStateLabel = props?.shellStateLabel;
    const shellSaveLabel = props?.shellSaveLabel;
    const shellApproveLabel = props?.shellApproveLabel;

    const emailIncorrectDataLabel = props?.emailIncorrectDataLabel;
    const emailHostnameLabel = props?.emailHostnameLabel;
    const emailUsernameLabel = props?.emailUsernameLabel;
    const emailUsernamePlaceholder = props?.emailUsernamePlaceholder;
    const emailResetLabel = props?.emailResetLabel;
    const emailStateLabel = props?.emailStateLabel;
    const emailSaveLabel = props?.emailSaveLabel;
    const emailApproveLabel = props?.emailApproveLabel;

    const virtualServerIncorrectDataLabel = props?.virtualServerIncorrectDataLabel;
    const virtualServerSystemLabel = props?.virtualServerSystemLabel;
    const virtualServerNameLabel = props?.virtualServerNameLabel;
    const virtualServerNamePlaceholder = props?.virtualServerNamePlaceholder;
    const virtualServerResetLabel = props?.virtualServerResetLabel;
    const virtualServerStateLabel = props?.virtualServerStateLabel;
    const virtualServerSaveLabel = props?.virtualServerSaveLabel;
    const virtualServerApproveLabel = props?.virtualServerApproveLabel;

    const webHotelIncorrectDataLabel = props?.webHotelIncorrectDataLabel;
    const webHotelSystemLabel = props?.webHotelSystemLabel;
    const webHotelNameLabel = props?.webHotelNameLabel;
    const webHotelNamePlaceholder = props?.webHotelNamePlaceholder;
    const webHotelResetLabel = props?.webHotelResetLabel;
    const webHotelStateLabel = props?.webHotelStateLabel;
    const webHotelSaveLabel = props?.webHotelSaveLabel;
    const webHotelApproveLabel = props?.webHotelApproveLabel;

    const domainIncorrectDataLabel = props?.domainIncorrectDataLabel;
    const domainNameLabel = props?.domainNameLabel;
    const domainResetLabel = props?.domainResetLabel;
    const domainStateLabel = props?.domainStateLabel;
    const domainSaveLabel = props?.domainSaveLabel;
    const domainApproveLabel = props?.domainApproveLabel;

    const domainTransferIncorrectDataLabel = props?.domainTransferIncorrectDataLabel;
    const domainTransferNameLabel = props?.domainTransferNameLabel;
    const domainTransferResetLabel = props?.domainTransferResetLabel;
    const domainTransferStateLabel = props?.domainTransferStateLabel;
    const domainTransferSaveLabel = props?.domainTransferSaveLabel;
    const domainTransferApproveLabel = props?.domainTransferApproveLabel;
    const domainTransferAuthIdLabel = props?.domainTransferAuthIdLabel;

    if (value === undefined) return <Loader />
    switch (type) {

        case ProductType.VIRTUAL_SERVER :
            return (
                <MyVirtualServerInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={virtualServerStateLabel}
                    resetLabel={virtualServerResetLabel}
                    approveLabel={virtualServerApproveLabel}
                    saveLabel={virtualServerSaveLabel}
                    incorrectDataLabel={virtualServerIncorrectDataLabel}
                    systemLabel={virtualServerSystemLabel}
                    nameLabel={virtualServerNameLabel}
                    namePlaceholder={virtualServerNamePlaceholder}
                />
            );

        case ProductType.WEB_HOTEL      :
            return (
                <MyWebHotelInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={webHotelStateLabel}
                    resetLabel={webHotelResetLabel}
                    approveLabel={webHotelApproveLabel}
                    saveLabel={webHotelSaveLabel}
                    incorrectDataLabel={webHotelIncorrectDataLabel}
                    systemLabel={webHotelSystemLabel}
                    nameLabel={webHotelNameLabel}
                    namePlaceholder={webHotelNamePlaceholder}
                />
            );

        case ProductType.EMAIL          :
            return (
                <MyEmailInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={emailStateLabel}
                    resetLabel={emailResetLabel}
                    approveLabel={emailApproveLabel}
                    saveLabel={emailSaveLabel}
                    incorrectDataLabel={emailIncorrectDataLabel}
                    hostnameLabel={emailHostnameLabel}
                    usernameLabel={emailUsernameLabel}
                    usernamePlaceholder={emailUsernamePlaceholder}
                />
            );

        case ProductType.SHELL          :
            return (
                <MyShellInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={shellStateLabel}
                    resetLabel={shellResetLabel}
                    approveLabel={shellApproveLabel}
                    saveLabel={shellSaveLabel}
                    incorrectDataLabel={shellIncorrectDataLabel}
                    hostnameLabel={shellHostnameLabel}
                    portLabel={shellPortLabel}
                    usernameLabel={shellUsernameLabel}
                    usernamePlaceholder={shellUsernamePlaceholder}
                    realNameLabel={shellRealNameLabel}
                    realNameDisclaimer={shellRealNameDisclaimer}
                />
            );

        case ProductType.DOMAIN         :
            return (
                <MyDomainInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={domainStateLabel}
                    resetLabel={domainResetLabel}
                    approveLabel={domainApproveLabel}
                    saveLabel={domainSaveLabel}
                    incorrectDataLabel={domainIncorrectDataLabel}
                    nameLabel={domainNameLabel}
                />
            );

        case ProductType.DOMAIN_TRANSFER         :
            return (
                <MyDomainTransferInventoryDataForm
                    className={classNames}
                    state={state}
                    value={value}
                    save={submit}
                    stateValues={stateValues}
                    permissions={permissions}
                    stateLabel={domainTransferStateLabel}
                    resetLabel={domainTransferResetLabel}
                    approveLabel={domainTransferApproveLabel}
                    saveLabel={domainTransferSaveLabel}
                    incorrectDataLabel={domainTransferIncorrectDataLabel}
                    nameLabel={domainTransferNameLabel}
                    authIdLabel={domainTransferAuthIdLabel}
                />
            );

        case ProductType.DATABASE       :
            return null;

        default                         :
            return null;
    }
}
